import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { useClinic } from '@/contexts/ClinicContext';
import {
  getDoctors,
  getStaff,
  getDentalProcedures,
  getAdminTasks,
  getRooms,
  createDoctor,
  updateDoctor,
  deleteDoctor,
  createStaff,
  updateStaff,
  deleteStaff,
  createDentalProcedure,
  updateDentalProcedure,
  deleteDentalProcedure,
  createAdminTask,
  updateAdminTask,
  deleteAdminTask,
  createRoom,
  updateRoom,
  deleteRoom,
  Doctor,
  Staff,
  DentalProcedure,
  AdminTask,
  Room,
} from '@/services/adminService';

export const useAdminData = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentClinic } = useClinic();
  const clinicId = currentClinic?.id;

  // Queries
  const doctorsQuery = useQuery({
    queryKey: ['doctors', clinicId],
    queryFn: () => getDoctors(clinicId),
    enabled: !!clinicId,
  });

  const staffQuery = useQuery({
    queryKey: ['staff', clinicId],
    queryFn: () => getStaff(clinicId),
    enabled: !!clinicId,
  });

  const proceduresQuery = useQuery({
    queryKey: ['dental_procedures', clinicId],
    queryFn: () => getDentalProcedures(clinicId),
    enabled: !!clinicId,
  });

  const adminTasksQuery = useQuery({
    queryKey: ['admin_tasks', clinicId],
    queryFn: () => getAdminTasks(clinicId),
    enabled: !!clinicId,
  });

  const roomsQuery = useQuery({
    queryKey: ['rooms', clinicId],
    queryFn: () => getRooms(clinicId),
    enabled: !!clinicId,
  });

  // Doctor mutations
  const createDoctorMutation = useMutation({
    mutationFn: createDoctor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors', clinicId] });
      toast({ title: 'Success', description: 'Doctor created successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create doctor', variant: 'destructive' });
      console.error('Error creating doctor:', error);
    },
  });

  const updateDoctorMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Doctor> }) =>
      updateDoctor(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors', clinicId] });
      toast({ title: 'Success', description: 'Doctor updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update doctor', variant: 'destructive' });
      console.error('Error updating doctor:', error);
    },
  });

  const deleteDoctorMutation = useMutation({
    mutationFn: deleteDoctor,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['doctors', clinicId] });
      toast({ title: 'Success', description: 'Doctor deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete doctor', variant: 'destructive' });
      console.error('Error deleting doctor:', error);
    },
  });

  // Staff mutations
  const createStaffMutation = useMutation({
    mutationFn: createStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', clinicId] });
      toast({ title: 'Success', description: 'Staff member created successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create staff member', variant: 'destructive' });
      console.error('Error creating staff:', error);
    },
  });

  const updateStaffMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Staff> }) =>
      updateStaff(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', clinicId] });
      toast({ title: 'Success', description: 'Staff member updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update staff member', variant: 'destructive' });
      console.error('Error updating staff:', error);
    },
  });

  const deleteStaffMutation = useMutation({
    mutationFn: deleteStaff,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['staff', clinicId] });
      toast({ title: 'Success', description: 'Staff member deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete staff member', variant: 'destructive' });
      console.error('Error deleting staff:', error);
    },
  });

  // Procedure mutations
  const createProcedureMutation = useMutation({
    mutationFn: createDentalProcedure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dental_procedures', clinicId] });
      toast({ title: 'Success', description: 'Procedure created successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create procedure', variant: 'destructive' });
      console.error('Error creating procedure:', error);
    },
  });

  const updateProcedureMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<DentalProcedure> }) =>
      updateDentalProcedure(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dental_procedures', clinicId] });
      toast({ title: 'Success', description: 'Procedure updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update procedure', variant: 'destructive' });
      console.error('Error updating procedure:', error);
    },
  });

  const deleteProcedureMutation = useMutation({
    mutationFn: deleteDentalProcedure,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dental_procedures', clinicId] });
      toast({ title: 'Success', description: 'Procedure deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete procedure', variant: 'destructive' });
      console.error('Error deleting procedure:', error);
    },
  });

  // Admin task mutations
  const createAdminTaskMutation = useMutation({
    mutationFn: createAdminTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tasks', clinicId] });
      toast({ title: 'Success', description: 'Admin task created successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create admin task', variant: 'destructive' });
      console.error('Error creating admin task:', error);
    },
  });

  const updateAdminTaskMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<AdminTask> }) =>
      updateAdminTask(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tasks', clinicId] });
      toast({ title: 'Success', description: 'Admin task updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update admin task', variant: 'destructive' });
      console.error('Error updating admin task:', error);
    },
  });

  const deleteAdminTaskMutation = useMutation({
    mutationFn: deleteAdminTask,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tasks', clinicId] });
      toast({ title: 'Success', description: 'Admin task deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete admin task', variant: 'destructive' });
      console.error('Error deleting admin task:', error);
    },
  });

  // Room mutations
  const createRoomMutation = useMutation({
    mutationFn: createRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms', clinicId] });
      toast({ title: 'Success', description: 'Room created successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to create room', variant: 'destructive' });
      console.error('Error creating room:', error);
    },
  });

  const updateRoomMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<Room> }) =>
      updateRoom(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms', clinicId] });
      toast({ title: 'Success', description: 'Room updated successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to update room', variant: 'destructive' });
      console.error('Error updating room:', error);
    },
  });

  const deleteRoomMutation = useMutation({
    mutationFn: deleteRoom,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rooms', clinicId] });
      toast({ title: 'Success', description: 'Room deleted successfully' });
    },
    onError: (error) => {
      toast({ title: 'Error', description: 'Failed to delete room', variant: 'destructive' });
      console.error('Error deleting room:', error);
    },
  });

  return {
    // Data
    doctors: doctorsQuery.data || [],
    staff: staffQuery.data || [],
    procedures: proceduresQuery.data || [],
    adminTasks: adminTasksQuery.data || [],
    rooms: roomsQuery.data || [],
    
    // Loading states
    isLoadingDoctors: doctorsQuery.isLoading,
    isLoadingStaff: staffQuery.isLoading,
    isLoadingProcedures: proceduresQuery.isLoading,
    isLoadingAdminTasks: adminTasksQuery.isLoading,
    isLoadingRooms: roomsQuery.isLoading,
    
    // Error states
    doctorsError: doctorsQuery.error,
    staffError: staffQuery.error,
    proceduresError: proceduresQuery.error,
    adminTasksError: adminTasksQuery.error,
    roomsError: roomsQuery.error,
    
    // Mutations
    createDoctor: (doctor: Omit<Doctor, 'id' | 'created_at' | 'updated_at'>) => {
      if (clinicId) {
        createDoctorMutation.mutate({ ...doctor, clinic_id: clinicId });
      }
    },
    updateDoctor: updateDoctorMutation.mutate,
    deleteDoctor: deleteDoctorMutation.mutate,
    createStaff: (staff: Omit<Staff, 'id' | 'created_at' | 'updated_at'>) => {
      if (clinicId) {
        createStaffMutation.mutate({ ...staff, clinic_id: clinicId });
      }
    },
    updateStaff: updateStaffMutation.mutate,
    deleteStaff: deleteStaffMutation.mutate,
    createProcedure: (procedure: Omit<DentalProcedure, 'id' | 'created_at' | 'updated_at'>) => {
      if (clinicId) {
        createProcedureMutation.mutate({ ...procedure, clinic_id: clinicId });
      }
    },
    updateProcedure: updateProcedureMutation.mutate,
    deleteProcedure: deleteProcedureMutation.mutate,
    createAdminTask: (task: Omit<AdminTask, 'id' | 'created_at' | 'updated_at'>) => {
      if (clinicId) {
        createAdminTaskMutation.mutate({ ...task, clinic_id: clinicId });
      }
    },
    updateAdminTask: updateAdminTaskMutation.mutate,
    deleteAdminTask: deleteAdminTaskMutation.mutate,
    createRoom: (room: Omit<Room, 'id' | 'created_at' | 'updated_at'>) => {
      if (clinicId) {
        createRoomMutation.mutate({ ...room, clinic_id: clinicId });
      }
    },
    updateRoom: updateRoomMutation.mutate,
    deleteRoom: deleteRoomMutation.mutate,
  };
};
